import React, { useState } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, TablePagination, Select, MenuItem, Tooltip, IconButton,
    Chip, useMediaQuery,
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const MS_COLORS = {
    primary: '#2D4A3D',
    primaryLight: '#EBF5EE',
    text: '#323130',
    textSecondary: '#605D5C',
    background: '#FFFFFF',
    backgroundLight: '#F3F2F1',
    border: '#E1E1E1',
    hoverBackground: '#F0F0F0',
    accent: '#107C10',
    red: '#D83B01',
};

const headerCellSx = { fontWeight: 'bold', fontSize: '0.85rem', whiteSpace: 'nowrap', color: MS_COLORS.text };
const bodyCellSx = { fontSize: '0.8rem', color: MS_COLORS.textSecondary, whiteSpace: 'nowrap' };

const statusChip = (status) => {
    const map = {
        active:    { label: 'Active',    color: MS_COLORS.accent,   bg: '#EBF5EB' },
        cancelled: { label: 'Cancelled', color: MS_COLORS.textSecondary, bg: '#F3F2F1' },
        failed:    { label: 'Failed',    color: MS_COLORS.red,      bg: '#FEF0EB' },
    };
    const s = map[status] || { label: status, color: MS_COLORS.text, bg: '#F3F2F1' };
    return (
        <Chip
            label={s.label}
            size="small"
            sx={{
                backgroundColor: s.bg,
                color: s.color,
                fontWeight: 'bold',
                fontSize: '0.72rem',
                height: 22,
            }}
        />
    );
};

const fmt = (date) => date ? new Date(date).toLocaleDateString('en-GB') : '—';

const RecurringSubscriptionsTable = ({ subscriptions }) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [statusFilter, setStatusFilter] = useState('all');
    const isSmallScreen = useMediaQuery('(max-width:600px)');
    const navigate = useNavigate();

    const filtered = statusFilter === 'all'
        ? subscriptions
        : subscriptions.filter(s => s.status === statusFilter);

    return (
        <Box
            sx={{
                p: 2,
                backgroundColor: MS_COLORS.background,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                mt: 3,
                border: `1px solid ${MS_COLORS.border}`,
            }}
        >
            <Typography
                variant="h6"
                sx={{
                    color: MS_COLORS.text,
                    mb: 3,
                    fontWeight: 'bold',
                    fontSize: isSmallScreen ? '1.1rem' : '1.4rem',
                }}
            >
                Recurring Subscriptions
            </Typography>

            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Select
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                    size="small"
                    sx={{
                        minWidth: 150,
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: MS_COLORS.border },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: MS_COLORS.primary },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: MS_COLORS.primary },
                        '& .MuiSelect-select': { color: MS_COLORS.text, fontSize: '0.875rem' },
                    }}
                >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                </Select>
            </Box>

            {filtered.length === 0 ? (
                <Box
                    sx={{
                        textAlign: 'center',
                        mt: 3,
                        p: 3,
                        border: `1px dashed ${MS_COLORS.border}`,
                        borderRadius: 1,
                        bgcolor: MS_COLORS.backgroundLight,
                    }}
                >
                    <Typography sx={{ color: MS_COLORS.textSecondary, fontSize: '0.9rem' }}>
                        No recurring subscriptions found.
                    </Typography>
                </Box>
            ) : (
                <>
                    <TableContainer
                        component={Paper}
                        sx={{
                            borderRadius: 1,
                            boxShadow: 0,
                            overflowX: 'auto',
                            border: `1px solid ${MS_COLORS.border}`,
                        }}
                    >
                        <Table sx={{ minWidth: 1100 }}>
                            <TableHead sx={{ bgcolor: MS_COLORS.backgroundLight }}>
                                <TableRow>
                                    {[
                                        'Donor Name', 'Donor Email', 'Campaign', 'Amount (UGX)',
                                        'Frequency', 'Total Donations', 'Subscription Date',
                                        'Last Donation Date', 'Next Donation Date', 'Status', 'Actions',
                                    ].map((h) => (
                                        <TableCell key={h} sx={headerCellSx}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filtered
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((sub) => (
                                        <TableRow
                                            key={sub.id}
                                            sx={{
                                                '&:last-child td, &:last-child th': { border: 0 },
                                                '&:hover': { backgroundColor: MS_COLORS.hoverBackground },
                                            }}
                                        >
                                            <TableCell sx={bodyCellSx}>{sub.donor_name}</TableCell>
                                            <TableCell sx={bodyCellSx}>{sub.donor_email}</TableCell>
                                            <TableCell sx={bodyCellSx}>{sub.campaign_name}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                UGX {Number(sub.amount).toLocaleString()}
                                            </TableCell>
                                            <TableCell sx={{ ...bodyCellSx, textTransform: 'capitalize' }}>
                                                {sub.frequency}
                                            </TableCell>
                                            <TableCell sx={bodyCellSx}>{sub.total_donations ?? 0}</TableCell>
                                            <TableCell sx={bodyCellSx}>{fmt(sub.created_at)}</TableCell>
                                            <TableCell sx={bodyCellSx}>{fmt(sub.last_charge_date)}</TableCell>
                                            <TableCell sx={bodyCellSx}>{fmt(sub.next_charge_date)}</TableCell>
                                            <TableCell sx={{ py: 1 }}>{statusChip(sub.status)}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                <Tooltip title="View Details">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => navigate(`/admin/recurring-donations/${sub.id}`)}
                                                    >
                                                        <Visibility fontSize="small" sx={{ color: MS_COLORS.primary }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={filtered.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiTablePagination-actions': {
                                fontSize: '0.8rem',
                                color: MS_COLORS.textSecondary,
                            },
                        }}
                    />
                </>
            )}
        </Box>
    );
};

export default RecurringSubscriptionsTable;
